import React from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Skeleton,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useNavigate } from "react-router-dom";

const iconPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`;

const labelShimmer = keyframes`
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

const APPROVED_PERCENT_THRESHOLD = 80;

const AIEvidenceStrip = ({
  pendingCount = 0,
  myPendingCount = 0,
  checksCount = 0,
  approvedCount = 0,
  approvedPercent = 0,
  totalCount = 0,
  loading = false,
  hasIntegrations = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const tc = theme.customColors;
  const info = tc.status.info;

  const cardSx = {
    position: "relative",
    overflow: "hidden",
    borderRadius: "12px",
    border: `1px solid ${alpha(info.main, 0.16)}`,
    background: `linear-gradient(100deg, ${alpha(info.main, 0.05)} 0%, ${alpha(
      info.main,
      0.015,
    )} 42%, ${tc.background.main} 74%)`,
    boxShadow: tc.shadow.card,
    mb: 3,
  };

  const iconBadge = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: "9px",
    flexShrink: 0,
    background: `linear-gradient(135deg, ${info.main} 0%, ${tc.button.primary} 100%)`,
    boxShadow: `0 2px 8px ${alpha(info.main, 0.35)}`,
    animation: `${iconPulse} 2.8s ease-in-out infinite`,
  };

  const shimmerText = {
    background: `linear-gradient(90deg, ${tc.text.strong} 40%, ${info.main} 50%, ${tc.text.strong} 60%)`,
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: `${labelShimmer} 3.5s linear infinite`,
  };

  const rowSx = {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: 1.75,
    px: 2.25,
    py: 1.75,
    flexWrap: "wrap",
  };

  if (loading) {
    return (
      <Card sx={cardSx}>
        <Box sx={rowSx}>
          <Skeleton variant="rounded" width={30} height={30} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={260} height={18} />
            <Skeleton width={200} height={14} />
          </Box>
          <Skeleton variant="rounded" width={130} height={36} />
        </Box>
      </Card>
    );
  }

  if (!hasIntegrations) {
    return (
      <Card sx={cardSx}>
        <Box sx={rowSx}>
          <Box sx={iconBadge}>
            <TipsAndUpdatesIcon sx={{ fontSize: 17, color: tc.button.text }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography
              sx={{
                fontSize: "14.5px",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: tc.text.strong,
              }}
            >
              Let{" "}
              <Box component="span" sx={shimmerText}>
                Rego AI
              </Box>{" "}
              collect your evidence
              <Tooltip title="How Rego AI works" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    window.open("/admin/regoai", "_blank", "noopener")
                  }
                  sx={{
                    ml: 0.5,
                    p: 0.375,
                    color: tc.text.disabled,
                    verticalAlign: "baseline",
                    "&:hover": {
                      backgroundColor: alpha(info.main, 0.08),
                      color: info.text,
                    },
                  }}
                >
                  <ArrowOutwardIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
              Connect a source and Rego AI finds evidence for your checks. You
              approve what counts
            </Typography>
          </Box>
          <Button
            variant="text"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/integrations")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: theme.typography.body2.fontSize,
              letterSpacing: "-0.01em",
              borderRadius: "8px",
              px: 1.25,
              minHeight: 34,
              whiteSpace: "nowrap",
              color: info.text,
              "&:hover": { bgcolor: alpha(info.main, 0.08) },
            }}
          >
            Connect integration
          </Button>
        </Box>
      </Card>
    );
  }

  const showApprovedPercent =
    totalCount > 0 && approvedPercent >= APPROVED_PERCENT_THRESHOLD;

  if (!pendingCount && !showApprovedPercent) return null;

  return (
    <Card sx={cardSx}>
      <Box sx={rowSx}>
        <Box sx={iconBadge}>
          <TipsAndUpdatesIcon sx={{ fontSize: 17, color: tc.button.text }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: tc.text.strong,
            }}
          >
            <Box component="span" sx={shimmerText}>
              Rego AI
            </Box>{" "}
            {showApprovedPercent ? (
              <>
                evidence is{" "}
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: "tabular-nums", color: info.text }}
                >
                  {approvedPercent}%
                </Box>{" "}
                approved
              </>
            ) : (
              <>
                found{" "}
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: "tabular-nums", color: info.text }}
                >
                  {myPendingCount > 0 ? myPendingCount : pendingCount}
                </Box>{" "}
                evidence{" "}
                {(myPendingCount > 0 ? myPendingCount : pendingCount) === 1
                  ? "suggestion"
                  : "suggestions"}
                {myPendingCount > 0
                  ? " you can review"
                  : ", none you can review yet"}
              </>
            )}
            <Tooltip title="How Rego AI works" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  window.open("/admin/regoai", "_blank", "noopener")
                }
                sx={{
                  ml: 0.375,
                  p: 0.375,
                  color: tc.text.disabled,
                  verticalAlign: "middle",
                  transition: "transform 160ms ease, color 160ms ease",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: info.text,
                    transform: "translate(2px, -2px)",
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "color 160ms ease",
                    "&:hover": { transform: "none" },
                  },
                }}
              >
                <ArrowOutwardIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
            {showApprovedPercent
              ? `${approvedCount} of ${totalCount} suggestions accepted as evidence${
                  pendingCount > 0
                    ? `, ${pendingCount} still awaiting review`
                    : ""
                }`
              : myPendingCount > 0
                ? `${pendingCount} found in total across ${checksCount} ${
                    checksCount === 1 ? "check" : "checks"
                  }${
                    pendingCount > myPendingCount
                      ? `, the rest managed by other data owners`
                      : ""
                  }`
                : "All of them are managed by other data owners. Request access to review them"}
          </Typography>
        </Box>

        <Button
          variant="text"
          disableElevation
          endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
          onClick={() => navigate("/admin/controls?aiEvidence=mine")}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: theme.typography.body2.fontSize,
            letterSpacing: "-0.01em",
            borderRadius: "8px",
            px: 1.25,
            minHeight: 34,
            whiteSpace: "nowrap",
            color: info.text,
            "&:hover": { bgcolor: alpha(info.main, 0.08) },
          }}
        >
          {myPendingCount > 0 ? "Review evidence" : "Request access"}
        </Button>
      </Box>
    </Card>
  );
};

export default AIEvidenceStrip;
